<template>
  <component
    :is="href && !disabled ? 'a' : 'span'"
    :class="[
      'link',
      `link--${size}`,
      {
        'link--disabled': disabled,
        'link--skeleton': skeleton,
      },
    ]"
    :href="disabled ? null : href"
    :target="href && !disabled && openInNewTab ? '_blank' : null"
    :rel="href && !disabled && openInNewTab ? 'noopener noreferrer' : null"
    @click="onClick"
  >
    <slot>{{ label }}</slot>
  </component>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'Link',
  props: {
    label: {
      type: String,
      default: 'Link',
    },
    href: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'large',
      validator: value => ['large', 'small'].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    skeleton: {
      type: Boolean,
      default: false,
    },
    openInNewTab: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const onClick = event => {
      if (props.disabled) {
        event.preventDefault();
        return;
      }

      emit('click', event);
    };

    return { onClick };
  },
});
</script>

<style scoped>
.link {
  font-family: 'Jost', sans-serif;
  font-weight: 500;
  color: #492b7c;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
  display: inline-block;
}

.link:hover:not(.link--disabled):not(.link--skeleton) {
  color: #674e92;
}

.link:focus:not(.link--disabled):not(.link--skeleton) {
  color: #8672a8;
  outline: none;
}

.link:active:not(.link--disabled):not(.link--skeleton) {
  color: #a495bd;
}

.link--large {
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
}

.link--small {
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0px;
}

.link--disabled {
  color: #bdbdbd;
  cursor: not-allowed;
}

.link--skeleton {
  background: #eeeeee;
  color: transparent;
  pointer-events: none;
  padding-right: 1px;
  padding-left: 1px;
  border-radius: 2px;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
